import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #pendingTimeout = null;

  /** @type {boolean} */
  #closedDuringPendingAdd = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartAddStarted, this.#handleCartAddStarted);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handlePendingClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartAddStarted, this.#handleCartAddStarted);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handlePendingClose);
    this.#historyAbortController?.abort();
    if (this.#pendingTimeout) clearTimeout(this.#pendingTimeout);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAddStarted = () => {
    if (!this.hasAttribute('auto-open')) return;

    this.#setPendingState();
    this.showDialog();
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open') && !this.#closedDuringPendingAdd) {
      this.showDialog();
    }

    this.#closedDuringPendingAdd = false;
    if (this.#pendingTimeout) clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = window.setTimeout(this.#clearPendingState, 150);
  };

  #handleCartError = () => {
    this.#clearPendingState();
  };

  #handlePendingClose = () => {
    if (this.hasAttribute('data-bb-cart-pending')) {
      this.#closedDuringPendingAdd = true;
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #setPendingState() {
    if (this.#pendingTimeout) clearTimeout(this.#pendingTimeout);
    this.#closedDuringPendingAdd = false;
    this.toggleAttribute('data-bb-cart-pending', true);
    this.refs.dialog?.toggleAttribute('data-bb-cart-pending', true);
    this.refs.dialog?.setAttribute('aria-busy', 'true');
    this.#insertPendingRow();

    this.#pendingTimeout = window.setTimeout(this.#clearPendingState, 8000);
  }

  #clearPendingState = () => {
    if (this.#pendingTimeout) {
      clearTimeout(this.#pendingTimeout);
      this.#pendingTimeout = null;
    }
    this.removeAttribute('data-bb-cart-pending');
    this.refs.dialog?.removeAttribute('data-bb-cart-pending');
    this.refs.dialog?.removeAttribute('aria-busy');
    this.refs.dialog?.querySelectorAll('.bb-cart-pending-row').forEach((row) => row.remove());
  };

  #insertPendingRow() {
    const items = this.refs.dialog?.querySelector('.cart-drawer__items');
    if (!items || items.querySelector('.bb-cart-pending-row')) return;

    const row = document.createElement('div');
    row.className = 'bb-cart-pending-row';
    row.setAttribute('aria-hidden', 'true');
    row.innerHTML = `
      <span class="bb-cart-pending-row__image"></span>
      <span class="bb-cart-pending-row__details">
        <span class="bb-cart-pending-row__title"></span>
        <span class="bb-cart-pending-row__price"></span>
        <span class="bb-cart-pending-row__meta"></span>
        <span class="bb-cart-pending-row__qty"></span>
      </span>
    `;
    items.prepend(row);
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
